import { NoteType, ColorTheme } from '../types';

export interface Template {
    id: string;
    name: string;
    description: string;
    iconName: string;
    previewImage?: string; // New field for generated preview images
    category?: 'productivity' | 'planning' | 'creative' | 'meetings' | 'strategy';
    elements: {
        type: NoteType;
        x: number;
        y: number;
        width?: number;
        height?: number;
        content?: string;
        title?: string;
        colorTheme?: ColorTheme;
        meta?: Record<string, unknown>;
        items?: { text: string; done: boolean }[];
        todoItems?: { id: string; text: string; completed: boolean }[];
    }[];
}

export const TEMPLATES: Template[] = [
    // ==================== PRODUCTIVITY ====================
    {
        id: 'kanban',
        name: 'Kanban Board',
        description: 'Complete workflow tracking with Backlog to Done.',
        iconName: 'Layout',
        previewImage: '/templates/template_kanban_1767893827779.png',
        category: 'productivity',
        elements: [
            { type: NoteType.TEXT, x: 0, y: 0, width: 300, height: 60, title: '📥 Backlog', colorTheme: 'dark', content: '' },
            { type: NoteType.TEXT, x: 320, y: 0, width: 300, height: 60, title: '⚡ To Do', colorTheme: 'orange', content: '' },
            { type: NoteType.TEXT, x: 640, y: 0, width: 300, height: 60, title: '🚧 In Progress', colorTheme: 'blue', content: '' },
            { type: NoteType.TEXT, x: 960, y: 0, width: 300, height: 60, title: '👀 Review', colorTheme: 'purple', content: '' },
            { type: NoteType.TEXT, x: 1280, y: 0, width: 300, height: 60, title: '✅ Done', colorTheme: 'green', content: '' },
            { type: NoteType.STICKY, x: 0, y: 80, width: 250, height: 250, title: 'Project Info', meta: { stickyColor: 'sticky-yellow' }, content: 'Goal: \nDeadline: \nOwner: ' }
        ]
    },
    {
        id: 'weekly-planner',
        name: 'Weekly Planner',
        description: 'Master your week with goals, habits, and daily tasks.',
        iconName: 'Calendar',
        previewImage: '/templates/template_weekly_planner_1767893842160.png',
        category: 'productivity',
        elements: [
            // Top Row: High Level
            { type: NoteType.TEXT, x: 0, y: 0, width: 400, height: 200, title: '⭐ This Week Goals', colorTheme: 'yellow', content: '1. \n2. \n3. ' },
            { type: NoteType.TODO, x: 420, y: 0, width: 300, height: 200, title: '🔄 Habit Tracker', todoItems: [{ id: '1', text: 'Exercise', completed: false }, { id: '2', text: 'Read 30m', completed: false }] },

            // Days
            { type: NoteType.TEXT, x: 0, y: 220, width: 200, height: 400, title: 'Monday', colorTheme: 'blue', content: '' },
            { type: NoteType.TEXT, x: 220, y: 220, width: 200, height: 400, title: 'Tuesday', colorTheme: 'teal', content: '' },
            { type: NoteType.TEXT, x: 440, y: 220, width: 200, height: 400, title: 'Wednesday', colorTheme: 'green', content: '' },
            { type: NoteType.TEXT, x: 660, y: 220, width: 200, height: 400, title: 'Thursday', colorTheme: 'orange', content: '' },
            { type: NoteType.TEXT, x: 880, y: 220, width: 200, height: 400, title: 'Friday', colorTheme: 'red', content: '' },
            { type: NoteType.TEXT, x: 1100, y: 220, width: 200, height: 200, title: 'Weekend', colorTheme: 'purple', content: '' },
        ]
    },
    {
        id: 'priority-matrix',
        name: 'Eisenhower Matrix',
        description: 'Decide what to do, schedule, delegate, or delete.',
        iconName: 'Grid',
        previewImage: '/templates/template_priority_matrix_1767893883860.png',
        category: 'productivity',
        elements: [
            { type: NoteType.TEXT, x: 400, y: 0, width: 400, height: 350, title: '🔥 DO FIRST (Urgent & Important)', colorTheme: 'red', content: '' },
            { type: NoteType.TEXT, x: 820, y: 0, width: 400, height: 350, title: '📅 SCHEDULE (Important, Not Urgent)', colorTheme: 'blue', content: '' },
            { type: NoteType.TEXT, x: 400, y: 370, width: 400, height: 350, title: '⚡ DELEGATE (Urgent, Not Important)', colorTheme: 'orange', content: '' },
            { type: NoteType.TEXT, x: 820, y: 370, width: 400, height: 350, title: '🗑️ DELETE (Neither)', colorTheme: 'dark', content: '' },
            // Axes labels
            { type: NoteType.STICKY, x: 200, y: 150, width: 150, height: 100, title: 'IMPACT', meta: { stickyColor: 'sticky-white' }, content: 'High' },
            { type: NoteType.STICKY, x: 600, y: -100, width: 150, height: 100, title: 'URGENCY', meta: { stickyColor: 'sticky-white' }, content: 'High' }
        ]
    },

    // ==================== MEETINGS ====================
    {
        id: 'meeting',
        name: 'Meeting Minutes',
        description: 'Professional structure for effective meetings.',
        iconName: 'Clipboard',
        previewImage: '/templates/template_meeting_notes_1767893907906.png',
        category: 'meetings',
        elements: [
            { type: NoteType.TEXT, x: 0, y: 0, width: 800, height: 120, title: 'Meeting Details', content: 'Topic: \nDate: \nAttendees: ', colorTheme: 'dark' },
            { type: NoteType.LIST, x: 0, y: 140, width: 380, height: 400, title: '📅 Agenda', items: [{ text: 'Review previous actions', done: false }, { text: 'Main discussion', done: false }] },
            { type: NoteType.TEXT, x: 400, y: 140, width: 400, height: 400, title: '📝 Discussion Notes', colorTheme: 'white', content: 'Key points:' },
            { type: NoteType.TODO, x: 820, y: 140, width: 300, height: 400, title: '✅ Action Items', todoItems: [] }
        ]
    },
    {
        id: 'daily-standup',
        name: 'Daily Standup',
        description: 'Quick sync for agile teams.',
        iconName: 'Users',
        previewImage: '/templates/template_daily_standup_1767893924598.png',
        category: 'meetings',
        elements: [
            { type: NoteType.TEXT, x: 0, y: 0, width: 300, height: 400, title: '✅ Completed Yesterday', colorTheme: 'green', content: '' },
            { type: NoteType.TEXT, x: 320, y: 0, width: 300, height: 400, title: '📋 Plan for Today', colorTheme: 'blue', content: '' },
            { type: NoteType.TEXT, x: 640, y: 0, width: 300, height: 400, title: '🚧 Blockers', colorTheme: 'red', content: '' },
            { type: NoteType.STICKY, x: 960, y: 0, width: 250, height: 250, title: 'Parking Lot', meta: { stickyColor: 'sticky-yellow' }, content: 'Items for later discussion:' }
        ]
    },
    {
        id: 'retro',
        name: 'Sprint Retrospective',
        description: 'Start/Stop/Continue analysis.',
        iconName: 'RefreshCw',
        previewImage: '/templates/template_retro_1767893955443.png',
        category: 'meetings',
        elements: [
            { type: NoteType.TEXT, x: 0, y: 0, width: 300, height: 350, title: '🟢 Start Doing', colorTheme: 'green', content: '' },
            { type: NoteType.TEXT, x: 320, y: 0, width: 300, height: 350, title: '🔴 Stop Doing', colorTheme: 'red', content: '' },
            { type: NoteType.TEXT, x: 640, y: 0, width: 300, height: 350, title: '🔵 Continue Doing', colorTheme: 'blue', content: '' },
            { type: NoteType.STICKY, x: 0, y: 370, width: 300, height: 200, title: '🎉 Kudos', meta: { stickyColor: 'sticky-pink' } },
            { type: NoteType.TODO, x: 320, y: 370, width: 620, height: 200, title: 'Action Plan', todoItems: [] }
        ]
    },

    // ==================== STRATEGY & BUSINESS ====================
    {
        id: 'business-model',
        name: 'Business Model Canvas',
        description: 'Strategic management template for developing new business models.',
        iconName: 'Briefcase',
        // previewImage: '/templates/template_bmc.png', // Image missing
        category: 'strategy',
        elements: [
            // Left Side (Efficiency)
            { type: NoteType.TEXT, x: 0, y: 0, width: 250, height: 400, title: 'Key Partners', colorTheme: 'white', content: '' },
            { type: NoteType.TEXT, x: 260, y: 0, width: 250, height: 190, title: 'Key Activities', colorTheme: 'white', content: '' },
            { type: NoteType.TEXT, x: 260, y: 210, width: 250, height: 190, title: 'Key Resources', colorTheme: 'white', content: '' },

            // Center (Value)
            { type: NoteType.TEXT, x: 520, y: 0, width: 250, height: 400, title: 'Value Propositions', colorTheme: 'yellow', content: '' },

            // Right Side (Market)
            { type: NoteType.TEXT, x: 780, y: 0, width: 250, height: 190, title: 'Customer Relationships', colorTheme: 'white', content: '' },
            { type: NoteType.TEXT, x: 780, y: 210, width: 250, height: 190, title: 'Channels', colorTheme: 'white', content: '' },
            { type: NoteType.TEXT, x: 1040, y: 0, width: 250, height: 400, title: 'Customer Segments', colorTheme: 'white', content: '' },

            // Bottom (Finance)
            { type: NoteType.TEXT, x: 0, y: 420, width: 640, height: 150, title: 'Cost Structure', colorTheme: 'orange', content: '' },
            { type: NoteType.TEXT, x: 650, y: 420, width: 640, height: 150, title: 'Revenue Streams', colorTheme: 'green', content: '' },
        ]
    },
    {
        id: 'user-persona',
        name: 'User Persona',
        description: 'Deep dive into user needs and frustrations.',
        iconName: 'User',
        // previewImage: '/templates/template_persona.png', // Image missing
        category: 'strategy',
        elements: [
            { type: NoteType.IMAGE, x: 0, y: 0, width: 300, height: 300, meta: { imageUrl: '' } }, // Placeholder for photo
            { type: NoteType.TEXT, x: 320, y: 0, width: 500, height: 140, title: 'Bio & Demographics', colorTheme: 'blue', content: 'Name: \nAge: \nOccupation: \nLocation: ' },
            { type: NoteType.TEXT, x: 320, y: 160, width: 500, height: 140, title: 'Personality', colorTheme: 'purple', content: 'Introvert/Extrovert: \nThinking/Feeling: ' },

            { type: NoteType.TEXT, x: 0, y: 320, width: 400, height: 250, title: 'Goals & Needs', colorTheme: 'green', content: '• \n• ' },
            { type: NoteType.TEXT, x: 420, y: 320, width: 400, height: 250, title: 'Frustrations & Pain Points', colorTheme: 'red', content: '• \n• ' },
        ]
    },
    {
        id: 'empathy-map',
        name: 'Empathy Map',
        description: 'Visualize what users say, think, do, and feel.',
        iconName: 'Heart',
        // previewImage: '/templates/template_empathy.png', // Image missing
        category: 'strategy',
        elements: [
            { type: NoteType.STICKY, x: 400, y: 250, width: 200, height: 200, title: 'USER', meta: { stickyColor: 'sticky-white' } }, // Center
            { type: NoteType.TEXT, x: 400, y: 0, width: 300, height: 230, title: 'SAYS', colorTheme: 'white', content: '"..."' },
            { type: NoteType.TEXT, x: 720, y: 200, width: 300, height: 300, title: 'DOES', colorTheme: 'white', content: '' },
            { type: NoteType.TEXT, x: 400, y: 520, width: 300, height: 230, title: 'THINKS', colorTheme: 'white', content: '' },
            { type: NoteType.TEXT, x: 80, y: 200, width: 300, height: 300, title: 'FEELS', colorTheme: 'white', content: '' },
        ]
    },

    // ==================== CREATIVE ====================
    {
        id: 'brainstorm',
        name: 'Brainstorming Session',
        description: 'Free-form area with plenty of sticky notes.',
        iconName: 'Zap',
        previewImage: '/templates/template_brainstorm_1767893991839.png',
        category: 'creative',
        elements: [
            { type: NoteType.TEXT, x: 350, y: 0, width: 400, height: 100, title: '💡 PROMPT / TOPIC', colorTheme: 'yellow', content: '' },
            // Scattered stickies
            { type: NoteType.STICKY, x: 0, y: 150, width: 220, height: 220, title: 'Idea', meta: { stickyColor: 'sticky-yellow' } },
            { type: NoteType.STICKY, x: 240, y: 150, width: 220, height: 220, title: 'Idea', meta: { stickyColor: 'sticky-blue' } },
            { type: NoteType.STICKY, x: 480, y: 150, width: 220, height: 220, title: 'Idea', meta: { stickyColor: 'sticky-pink' } },
            { type: NoteType.STICKY, x: 720, y: 150, width: 220, height: 220, title: 'Idea', meta: { stickyColor: 'sticky-green' } },
            { type: NoteType.STICKY, x: 120, y: 400, width: 220, height: 220, title: 'Idea', meta: { stickyColor: 'sticky-orange' } },
            { type: NoteType.STICKY, x: 360, y: 400, width: 220, height: 220, title: 'Idea', meta: { stickyColor: 'sticky-purple' } },
            { type: NoteType.STICKY, x: 600, y: 400, width: 220, height: 220, title: 'Idea', meta: { stickyColor: 'sticky-teal' } }
        ]
    },
    {
        id: 'mood-board',
        name: 'Mood Board',
        description: 'Visual collage for design inspiration.',
        iconName: 'Image',
        previewImage: '/templates/template_mood_board_1767894031523.png',
        category: 'creative',
        elements: [
            { type: NoteType.TEXT, x: 0, y: 0, width: 800, height: 80, title: '🎨 Concept Theme', colorTheme: 'dark', content: '' },
            // Image placeholders
            { type: NoteType.IMAGE, x: 0, y: 100, width: 400, height: 300, meta: { imageUrl: '' } },
            { type: NoteType.IMAGE, x: 420, y: 100, width: 250, height: 190, meta: { imageUrl: '' } },
            { type: NoteType.IMAGE, x: 420, y: 310, width: 250, height: 190, meta: { imageUrl: '' } },
            { type: NoteType.IMAGE, x: 690, y: 100, width: 250, height: 400, meta: { imageUrl: '' } },
            // Palette
            { type: NoteType.STICKY, x: 0, y: 420, width: 120, height: 120, title: '', meta: { stickyColor: 'sticky-blue' } },
            { type: NoteType.STICKY, x: 130, y: 420, width: 120, height: 120, title: '', meta: { stickyColor: 'sticky-green' } },
            { type: NoteType.STICKY, x: 260, y: 420, width: 120, height: 120, title: '', meta: { stickyColor: 'sticky-yellow' } },
            { type: NoteType.STICKY, x: 390, y: 520, width: 200, height: 120, title: 'Typography', meta: { stickyColor: 'sticky-white' }, content: 'Font: Inter' },
        ]
    },

    // ==================== PLANNING ====================
    {
        id: 'roadmap',
        name: 'Quarterly Roadmap',
        description: 'High-level timeline for product direction.',
        iconName: 'TrendingUp',
        previewImage: '/templates/template_roadmap_1767894138224.png',
        category: 'planning',
        elements: [
            { type: NoteType.TEXT, x: 0, y: 0, width: 300, height: 80, title: 'Q1: Foundation', colorTheme: 'blue', content: 'Focus: ' },
            { type: NoteType.TEXT, x: 320, y: 0, width: 300, height: 80, title: 'Q2: Growth', colorTheme: 'teal', content: 'Focus: ' },
            { type: NoteType.TEXT, x: 640, y: 0, width: 300, height: 80, title: 'Q3: Scale', colorTheme: 'green', content: 'Focus: ' },
            { type: NoteType.TEXT, x: 960, y: 0, width: 300, height: 80, title: 'Q4: Expansion', colorTheme: 'yellow', content: 'Focus: ' },

            // Swimlanes implied by vertical positioning
            { type: NoteType.TEXT, x: -150, y: 100, width: 140, height: 60, title: 'Frontend', colorTheme: 'dark', content: '' },
            { type: NoteType.TEXT, x: -150, y: 250, width: 140, height: 60, title: 'Backend', colorTheme: 'dark', content: '' },
            { type: NoteType.TEXT, x: -150, y: 400, width: 140, height: 60, title: 'Design', colorTheme: 'dark', content: '' },

            // Items
            { type: NoteType.STICKY, x: 0, y: 100, width: 200, height: 120, title: 'Feature A', meta: { stickyColor: 'sticky-blue' } },
            { type: NoteType.STICKY, x: 320, y: 250, width: 200, height: 120, title: 'Feature B', meta: { stickyColor: 'sticky-green' } },
        ]
    },
    {
        id: 'okr',
        name: 'OKR Dashboard',
        description: 'Objectives and Key Results tracking.',
        iconName: 'Target',
        previewImage: '/templates/template_okr_1767894158711.png',
        category: 'planning',
        elements: [
            { type: NoteType.TEXT, x: 0, y: 0, width: 800, height: 100, title: '🎯 OBJECTIVE: [Enter Big Audacious Goal]', colorTheme: 'dark', content: '' },

            { type: NoteType.TEXT, x: 0, y: 140, width: 250, height: 300, title: 'Key Result 1', colorTheme: 'blue', content: 'Measure: \nTarget: ' },
            { type: NoteType.TEXT, x: 270, y: 140, width: 250, height: 300, title: 'Key Result 2', colorTheme: 'blue', content: 'Measure: \nTarget: ' },
            { type: NoteType.TEXT, x: 540, y: 140, width: 250, height: 300, title: 'Key Result 3', colorTheme: 'blue', content: 'Measure: \nTarget: ' },

            { type: NoteType.TODO, x: 820, y: 140, width: 300, height: 300, title: 'Initiatives', todoItems: [] },
        ]
    }
];

// Helper to get templates by category
export const getTemplatesByCategory = (category: Template['category']) =>
    TEMPLATES.filter(t => t.category === category);

// Get all unique categories - Updated icons
export const TEMPLATE_CATEGORIES = [
    { id: 'productivity', name: 'Productivity', icon: 'CheckCircle' }, // Changed to CheckCircle
    { id: 'meetings', name: 'Meetings', icon: 'Users' },
    { id: 'creative', name: 'Creative', icon: 'Palette' },
    { id: 'strategy', name: 'Strategy', icon: 'Crosshair' }, // Changed to Crosshair
    { id: 'planning', name: 'Planning', icon: 'Calendar' },
] as const;
